import { ArrowsClockwiseIcon, UploadSimpleIcon } from '@phosphor-icons/react'
import React, { useEffect, useState } from 'react'

import { Button } from '@/components/ui/button'
import { Dropzone } from '@/components/ui/dropzone'
import { ComponentVisualState } from '@/components/ui/dropzone/dropzone.types'
import { FileItem } from '@/components/ui/file-item'
import { Icon } from '@/components/ui/icon'
import { Typography } from '@/components/ui/typography'
import { NullableType } from '@/models'

import * as S from './file-upload.styled'
import { FileUploadMode, FileUploadSize } from './file-upload.types'
import { parseLabelParts } from './file-upload.utils'

interface MinimizedUploadProps {
  files: Array<File | string>
  onChange: (files: Array<File | string>) => void
  triggerMode?: FileUploadMode
  size: FileUploadSize
  multiple: boolean
  disabled: boolean
  atCapacity: boolean
  componentState: ComponentVisualState
  acceptedFilesText: string
  slotsText: NullableType<string>
  label?: string
  required?: boolean
  description?: string
  error?: string
  withReservedFooter?: boolean
  uploadLabel: string
  showBrowseLibrary?: boolean
  onBrowseLibrary?: () => void
  onInputClick: () => void
  onDragEnter: (e: React.DragEvent) => void
  onDragLeave: (e: React.DragEvent) => void
  onDragOver: (e: React.DragEvent) => void
  onDrop: (e: React.DragEvent) => void
}

const FileItemWithPreview = ({
  file,
  size,
  onClose,
}: {
  file: File | string
  size: FileUploadSize
  onClose: () => void
}) => {
  const [objectUrl, setObjectUrl] = useState<string | undefined>(undefined)

  useEffect(() => {
    if (typeof file === 'string' || !file.type.startsWith('image/')) return

    const url = URL.createObjectURL(file)
    setObjectUrl(url)

    return () => URL.revokeObjectURL(url)
  }, [file])

  const fileName = typeof file === 'string' ? (file.split('/').pop() ?? file) : file.name
  const fileType = fileName.split('.').pop()?.toUpperCase() ?? ''
  const previewUrl = typeof file === 'string' ? file : objectUrl

  return (
    <FileItem
      fileName={fileName}
      fileType={fileType}
      previewUrl={previewUrl}
      size={size}
      onClose={onClose}
    />
  )
}

export const MinimizedUpload = ({
  files,
  onChange,
  triggerMode,
  size,
  multiple,
  disabled,
  atCapacity,
  componentState,
  acceptedFilesText,
  slotsText,
  label,
  required,
  description,
  error,
  withReservedFooter,
  uploadLabel,
  showBrowseLibrary,
  onBrowseLibrary,
  onInputClick,
  onDragEnter,
  onDragLeave,
  onDragOver,
  onDrop,
}: MinimizedUploadProps) => {
  const [labelMain, labelBracket] = parseLabelParts(label)
  const shouldShowFooter = withReservedFooter || !!error

  return (
    <>
      {(labelMain || description) && (
        <S.MaximizedTextGroup>
          {labelMain && (
            <Typography size="heading-small" color="black800">
              {labelMain}
              {labelBracket && <S.LabelBracket> {labelBracket}</S.LabelBracket>}
              {required && <span aria-hidden="true"> *</span>}
            </Typography>
          )}
          {description && (
            <Typography size="text-large" color="black500">
              {description}
            </Typography>
          )}
        </S.MaximizedTextGroup>
      )}

      {triggerMode === 'button' ? (
        <S.ButtonTriggerWrap>
          <Button
            use="primary"
            size="medium"
            disabled={disabled || (atCapacity && multiple)}
            onClick={onInputClick}
          >
            <Icon icon={!multiple && files.length > 0 ? ArrowsClockwiseIcon : UploadSimpleIcon} />
            {!multiple && files.length > 0 ? 'Replace' : uploadLabel}
          </Button>
        </S.ButtonTriggerWrap>
      ) : (
        <Dropzone
          disabled={disabled}
          atCapacity={atCapacity}
          componentState={componentState}
          multiple={multiple}
          showBrowseLibrary={showBrowseLibrary}
          onBrowseLibrary={onBrowseLibrary}
          onInputClick={onInputClick}
          onDragEnter={onDragEnter}
          onDragLeave={onDragLeave}
          onDragOver={onDragOver}
          onDrop={onDrop}
        />
      )}

      {acceptedFilesText && (
        <S.AcceptedFilesText size="text-medium" color="black500" textAlign="center">
          {acceptedFilesText}
        </S.AcceptedFilesText>
      )}

      {files.length > 0 && (
        <S.FileList>
          {files.map((file, index) => (
            <FileItemWithPreview
              key={typeof file === 'string' ? file : file.name}
              file={file}
              size={size}
              onClose={() => onChange(files.filter((_, i) => i !== index))}
            />
          ))}
        </S.FileList>
      )}

      {!!slotsText && (
        <S.SlotsRemainingText size="text-medium" color="black500" textAlign="center">
          {slotsText}
        </S.SlotsRemainingText>
      )}

      {shouldShowFooter && (
        <S.Footer $size={size}>
          {error && (
            <S.Message $hasError role="alert">
              {error}
            </S.Message>
          )}
        </S.Footer>
      )}
    </>
  )
}
